// Utils: seo
// Purpose: Centralized SEO helpers for meta tags, structured data, sitemap
// generation, preload hints, and bot detection to avoid SEO-hostile redirects.

export const SEO_CONFIG = {
  siteName: "Advocacy Lab",
  siteUrl: "https://advocacylab.com",
  defaultImage: "/images/og-image.png",
  twitterHandle: "@advocacylab",
  defaultDescription: "The fastest way to launch high-impact political creative. Featuring 500+ templates for campaigns and causes. Create professional ads and graphics in minutes.",
  keywords: {
    primary: ["political creative", "campaign templates", "political ads", "advocacy marketing", "political graphics"],
    secondary: ["campaign design", "political campaign tools", "advocacy groups", "political creative templates", "election marketing"]
  }
};

// Enhanced bot detection for SEO purposes
export const isSearchEngineBot = () => {
  if (typeof window === 'undefined') return true;
  
  const userAgent = navigator.userAgent?.toLowerCase() || '';
  
  // Specific search engine bots that should never see redirects
  const searchEngineBots = [
    'googlebot',
    'google-inspectiontool', 
    'google-structured-data-testing-tool',
    'google-site-verification',
    'bingbot',
    'slurp', // Yahoo
    'duckduckbot',
    'baiduspider',
    'yandexbot',
    'applebot',
    'facebookexternalhit',
    'twitterbot',
    'linkedinbot',
    'whatsapp',
    'telegrambot'
  ];
  
  // SEO and performance testing tools
  const seoTools = [
    'lighthouse',
    'pagespeed',
    'gtmetrix',
    'pingdom',
    'webpagetest',
    'seobility',
    'semrush',
    'ahrefs',
    'moz',
    'majestic',
    'screaming frog',
    'deepcrawl',
    'sitebulb',
    'botify'
  ];
  
  const allBots = [...searchEngineBots, ...seoTools];
  const isBot = allBots.some(bot => userAgent.includes(bot));
  
  if (isBot) {
    console.log('🤖 Search engine bot detected:', userAgent);
    return true;
  }
  
  // Additional bot detection patterns
  const botPatterns = [
    /bot/i,
    /crawl/i,
    /spider/i,
    /index/i,
    /fetch/i
  ];
  
  const matchesPattern = botPatterns.some(pattern => pattern.test(userAgent));
  if (matchesPattern) {
    console.log('🤖 Bot pattern matched:', userAgent);
    return true;
  }
  
  return false;
};

// Enhanced function to check if we should prevent redirects for SEO
export const shouldPreventRedirectForSEO = () => {
  if (typeof window === 'undefined') return true;
  
  // Check if this is a search engine bot
  if (isSearchEngineBot()) {
    return true;
  }
  
  // Check for headless browser indicators (often used by crawlers)
  const isHeadless = !!(
    !window.outerHeight ||
    !window.outerWidth ||
    navigator.userAgent.includes('HeadlessChrome') ||
    navigator.webdriver ||
    window.phantom ||
    window.callPhantom
  );
  
  if (isHeadless) {
    console.log('🤖 Headless browser detected, preventing redirects for SEO');
    return true;
  }
  
  // Check referrer patterns that indicate crawling
  const referrer = document.referrer?.toLowerCase() || '';
  const crawlerReferrers = [
    'google.com/search',
    'bing.com/search',
    'duckduckgo.com',
    'search.yahoo.com'
  ];
  
  if (crawlerReferrers.some(ref => referrer.includes(ref))) {
    console.log('🔍 Search engine referrer detected, preventing redirects for SEO');
    return true;
  }
  
  return false;
};

// Check if current request is from a crawler that needs static content
export const needsStaticContent = () => {
  if (isSearchEngineBot()) return true;
  
  // Additional checks for headless browsers and automation
  const hasAutomationFlags = !!(
    navigator.webdriver ||
    window.phantom ||
    window.callPhantom ||
    window.__nightmare ||
    window.chrome?.runtime?.onConnect
  );
  
  // Check for missing standard browser features that indicate automation
  const missingBrowserFeatures = !!(
    !window.localStorage ||
    !window.sessionStorage ||
    !document.cookie ||
    !window.indexedDB
  );
  
  // Check for headless browser patterns
  const isHeadless = !!(
    !window.outerHeight ||
    !window.outerWidth ||
    navigator.userAgent.includes('HeadlessChrome')
  );
  
  return hasAutomationFlags || missingBrowserFeatures || isHeadless;
};

// Generate robot-friendly meta tags for preventing indexing of redirect pages
export const getRobotMetaTags = (shouldIndex = true, shouldFollow = true) => {
  const robots = [];
  if (shouldIndex) robots.push('index');
  else robots.push('noindex');
  
  if (shouldFollow) robots.push('follow');
  else robots.push('nofollow');
  
  return {
    robots: robots.join(', '),
    googlebot: robots.join(', '),
    'google-site-verification': 'noindex, nofollow', // Prevent verification pages from being indexed
  };
};

// Function to create canonical URLs that prevent duplicate content issues
export const getCanonicalUrl = (path = '') => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SEO_CONFIG.siteUrl}${cleanPath}`;
};

// Generate preload hints for critical resources to improve Core Web Vitals
export const getPreloadHints = () => {
  return [
    // Critical fonts
    {
      href: "https://use.typekit.net/qdq3zrl.css",
      as: "style",
      crossorigin: ""
    },
    // Critical images for LCP
    {
      href: "/images/hero/big-hero.jpeg", 
      as: "image",
      media: "(prefers-color-scheme: light)"
    },
    {
      href: "/images/hero/big-hero-dark.jpeg",
      as: "image", 
      media: "(prefers-color-scheme: dark)"
    },
    // Critical JavaScript
    {
      href: "/src/main.jsx",
      as: "script",
      type: "module"
    }
  ];
};

export const getPageSEO = (pageType, customData = {}) => {
  const baseConfig = {
    // Essential Meta Tags
    title: "",
    description: SEO_CONFIG.defaultDescription,
    keywords: [...SEO_CONFIG.keywords.primary, ...SEO_CONFIG.keywords.secondary].join(", "),
    canonicalUrl: SEO_CONFIG.siteUrl,
    image: SEO_CONFIG.defaultImage,
    
    // Open Graph
    ogType: "website",
    ogSiteName: SEO_CONFIG.siteName,
    
    // Twitter
    twitterCard: "summary_large_image",
    twitterSite: SEO_CONFIG.twitterHandle,
    
    // Structured Data
    structuredData: null,
    
    // Performance
    preloadImages: [],
    criticalCSS: "",
    
    // Additional
    robots: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    language: "en",
    author: "Advocacy Lab Team",
    
    // Anti-redirect meta tags for bots
    ...getRobotMetaTags(true, true)
  };

  const pageConfigs = {
    home: {
      title: "Advocacy Lab - High-Impact Political Creative for Campaigns",
      description: "The fastest way to launch high-impact political creative. Featuring 500+ templates for campaigns and causes. Create professional ads and graphics in minutes.",
      keywords: "political creative, campaign templates, political ads, advocacy marketing, political graphics, campaign design, political campaign tools, advocacy groups, election marketing, political design software",
      structuredData: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": "https://advocacylab.com/#organization",
            "name": "Advocacy Lab",
            "url": "https://advocacylab.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://advocacylab.com/images/logos/logo-big-colored.png",
              "width": 512,
              "height": 512
            },
            "description": "Professional political creative platform providing 500+ templates for campaigns and advocacy groups",
            "foundingDate": "2023",
            "industry": "Political Technology",
            "areaServed": "United States",
            "knowsAbout": ["Political Marketing", "Campaign Design", "Advocacy Graphics", "Election Materials"],
            "sameAs": [
              "https://twitter.com/advocacylab",
              "https://linkedin.com/company/advocacylab"
            ]
          },
          {
            "@type": "WebSite",
            "@id": "https://advocacylab.com/#website",
            "url": "https://advocacylab.com",
            "name": "Advocacy Lab",
            "description": "The fastest way to launch high-impact political creative. Featuring 500+ templates for campaigns and causes.",
            "publisher": {
              "@id": "https://advocacylab.com/#organization"
            },
            "inLanguage": "en-US",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://advocacylab.com/templates?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@type": "SoftwareApplication",
            "name": "Advocacy Lab",
            "applicationCategory": "Design Tool",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "description": "The fastest way to launch high-impact political creative. Featuring 500+ templates for campaigns, offices, and advocacy groups.",
            "featureList": [
              "500+ Political Templates",
              "Drag & Drop Editor", 
              "Multiple File Formats",
              "Brand Customization",
              "Print & Digital Ready"
            ],
            "screenshot": "https://advocacylab.com/images/og-image.png",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "250",
              "bestRating": "5"
            },
            "publisher": {
              "@id": "https://advocacylab.com/#organization"
            }
          }
        ]
      },
      preloadImages: [
        // Only preload the most critical image - hero image will be handled by getPreloadHints with media queries
        // GIF will be loaded on-demand when user interacts with features section
      ]
    },
    
    about: {
      title: "About Advocacy Lab - Political Creative Design Platform",
      description: "Learn about Advocacy Lab's mission to democratize political creative design. Discover how we're helping campaigns and advocacy groups create professional, high-impact graphics.",
      keywords: "about advocacy lab, political design platform, campaign creative team, advocacy marketing company, political graphic design mission",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "mainEntity": {
          "@type": "Organization",
          "name": "Advocacy Lab",
          "description": "A platform dedicated to democratizing political creative design for campaigns and advocacy groups.",
          "url": "https://advocacylab.com",
          "foundingDate": "2023",
          "industry": "Political Technology",
          "mission": "To make professional political creative accessible to all campaigns and causes"
        }
      },
      canonicalUrl: `${SEO_CONFIG.siteUrl}/about`
    },
    
    templates: {
      title: "500+ Political Campaign Templates - Advocacy Lab",
      description: "Browse our extensive library of 500+ professional political campaign templates. Create stunning ads, graphics, and marketing materials for your political campaign or advocacy group.",
      keywords: "political campaign templates, election templates, advocacy templates, political graphics library, campaign design templates, political ad templates, election marketing materials",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Political Campaign Templates",
        "description": "A comprehensive collection of professional political campaign templates for elections, advocacy, and political marketing.",
        "numberOfItems": "500+",
        "mainEntity": {
          "@type": "CreativeWork",
          "name": "Political Campaign Template Collection",
          "creator": {
            "@type": "Organization",
            "name": "Advocacy Lab"
          }
        }
      },
      canonicalUrl: `${SEO_CONFIG.siteUrl}/templates`
    },
    
    pricing: {
      title: "Pricing Plans - Advocacy Lab Political Creative Platform",
      description: "Choose the perfect plan for your political campaign or advocacy group. Free tier available. Affordable professional plans for campaigns of all sizes.",
      keywords: "political creative pricing, campaign design pricing, advocacy marketing costs, political template pricing, campaign tool pricing",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "PricingPage",
        "name": "Advocacy Lab Pricing",
        "offers": [
          {
            "@type": "Offer",
            "name": "Free Plan",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free access to basic political creative templates"
          },
          {
            "@type": "Offer",
            "name": "Premium Plan",
            "price": "29",
            "priceCurrency": "USD",
            "billingIncrement": "month",
            "description": "Full access to all templates and premium features"
          }
        ]
      },
      canonicalUrl: `${SEO_CONFIG.siteUrl}/pricing`
    },
    
    doneForYou: {
      title: "Done-For-You Political Creative Services - Advocacy Lab",
      description: "Get professional political creative designed by experts. Our done-for-you service creates custom graphics, ads, and marketing materials for your campaign.",
      keywords: "political creative services, campaign design services, political graphic design, custom political ads, campaign marketing services",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Done-For-You Political Creative",
        "provider": {
          "@type": "Organization",
          "name": "Advocacy Lab"
        },
        "description": "Professional political creative design services for campaigns and advocacy groups",
        "serviceType": "Graphic Design",
        "category": "Political Marketing"
      },
      canonicalUrl: `${SEO_CONFIG.siteUrl}/done-for-you`
    },
    
    demo: {
      title: "Demo - See Advocacy Lab Templates in Action",
      description: "Explore live examples of our political campaign templates. See how easy it is to create professional political graphics and ads with Advocacy Lab.",
      keywords: "political template demo, campaign design examples, political graphics showcase, advocacy marketing examples",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/demo`
    },
    
    
    login: {
      title: "Sign In - Advocacy Lab | Political Creative Platform",
      description: "Sign in to Advocacy Lab to access 500+ political creative templates, manage your campaigns, and scale your creative output. Your political design workspace awaits.",
      keywords: "advocacy lab login, political creative account, manage templates, subscription settings, campaign design login",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/login`,
      robots: "noindex, follow"
    },
    signin: {
      title: "Sign In - Advocacy Lab | Political Creative Platform",
      description: "Sign in to Advocacy Lab to access 500+ political creative templates, manage your campaigns, and scale your creative output. Your political design workspace awaits.",
      keywords: "advocacy lab login, political creative account, manage templates, subscription settings, campaign design login",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/signin`,
      robots: "noindex, follow"
    },
    signup: {
      title: "Sign Up - Advocacy Lab | Political Creative Platform",
      description: "Join Advocacy Lab and get access to 500+ political creative templates. Create professional campaign graphics and scale your political creative output.",
      keywords: "advocacy lab signup, political creative account, register for templates, campaign design registration",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/signup`,
      robots: "noindex, follow"
    },
    'forgot-password': {
      title: "Reset Password - Advocacy Lab",
      description: "Reset your Advocacy Lab account password to regain access to your political creative templates and account settings.",
      keywords: "advocacy lab password reset, forgot password, account recovery",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/forgot-password`,
      robots: "noindex, follow"
    },
    'verify-email': {
      title: "Verify Email - Advocacy Lab",
      description: "Verify your email address to complete your Advocacy Lab account setup and access all platform features.",
      keywords: "advocacy lab email verification, account verification, enterprise verification",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/verify-email`,
      robots: "noindex, follow"
    },
    // Enterprise pages - should be noindex since they're private/restricted
    'enterprise-signup': {
      title: "Enterprise Sign Up - Advocacy Lab",
      description: "Join your organization's Advocacy Lab enterprise account. Access exclusive templates and enterprise features.",
      keywords: "advocacy lab enterprise signup, organization account, enterprise registration",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/enterprise/signup`,
      robots: "noindex, follow"
    },
    'enterprise-signin': {
      title: "Enterprise Sign In - Advocacy Lab", 
      description: "Sign in to your organization's Advocacy Lab enterprise account. Access your team's political creative workspace.",
      keywords: "advocacy lab enterprise signin, organization login, enterprise account access",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/enterprise/signin`,
      robots: "noindex, follow"
    },
    // App pages - all should be noindex since they require authentication
    'app': {
      title: "Dashboard - Advocacy Lab",
      description: "Your Advocacy Lab dashboard. Access templates, manage campaigns, and create political creative content.",
      keywords: "advocacy lab dashboard, political templates, campaign management",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app`,
      robots: "noindex, follow"
    },
    'app-templates': {
      title: "Templates - Advocacy Lab",
      description: "Browse and select from our 500+ collection of high-converting templates for your advocacy campaigns.",
      keywords: "advocacy lab templates, political templates, campaign templates, template library",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/`,
      robots: "noindex, follow"
    },
    'app-video-templates': {
      title: "Video Templates - Advocacy Lab",
      description: "Browse and select from our collection of professional video templates for your advocacy campaigns.",
      keywords: "advocacy lab video templates, political video templates, campaign video templates, video template library",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/videoTemplates`,
      robots: "noindex, follow"
    },
    'app-stock-videos': {
      title: "Stock Videos - Advocacy Lab",
      description: "Browse and download from our collection of professional stock videos for your advocacy campaigns.",
      keywords: "advocacy lab stock videos, political stock videos, campaign stock videos, video library",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/stockVideos`,
      robots: "noindex, follow"
    },
    'app-campaigns': {
      title: "Campaigns - Advocacy Lab",
      description: "Manage your advocacy campaigns and collect testimonials from supporters.",
      keywords: "advocacy lab campaigns, political campaigns, testimonial campaigns, campaign management",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/campaigns`,
      robots: "noindex, follow"
    },
    'app-create-campaign': {
      title: "Create Campaign - Advocacy Lab",
      description: "Create a new advocacy campaign to collect testimonials and feedback from supporters.",
      keywords: "create campaign, new campaign, testimonial collection, advocacy campaign",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/campaigns/new`,
      robots: "noindex, follow"
    },
    'app-copy-analyzer': {
      title: "Copy Analyzer - Advocacy Lab",
      description: "Analyze and improve your campaign copy with AI-powered suggestions and recommendations.",
      keywords: "copy analyzer, AI copy analysis, campaign copy improvement, political messaging",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/copyAnalyzer`,
      robots: "noindex, follow"
    },
    'app-done-for-you': {
      title: "Done For You - Advocacy Lab",
      description: "Professional done-for-you services for your advocacy campaigns and creative needs.",
      keywords: "done for you services, professional campaign services, advocacy consulting",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/doneForYou`,
      robots: "noindex, follow"
    },
    
    // Removed custom templates SEO entries
    'app-account': {
      title: "Account Settings - Advocacy Lab",
      description: "Manage your account settings, subscription, and billing information.",
      keywords: "account settings, subscription management, billing, account preferences",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/account`,
      robots: "noindex, follow"
    },
    'app-profile': {
      title: "Edit Profile - Advocacy Lab",
      description: "Update your profile information, password, and account preferences.",
      keywords: "edit profile, update profile, change password, account information",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/profile`,
      robots: "noindex, follow"
    },
    'app-support': {
      title: "Support - Advocacy Lab",
      description: "Get help and support for your Advocacy Lab account and campaigns.",
      keywords: "advocacy lab support, help, customer service, technical support",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/account/support`,
      robots: "noindex, follow"
    },
    'app-namespaces': {
      title: "Organizations - Advocacy Lab",
      description: "Manage your organizations and team access in Advocacy Lab.",
      keywords: "advocacy lab organizations, team management, namespaces, collaboration",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/namespaces`,
      robots: "noindex, follow"
    },
    'app-create-namespace': {
      title: "Create Organization - Advocacy Lab",
      description: "Create a new organization to collaborate with your team on advocacy campaigns.",
      keywords: "advocacy lab create organization, new team, collaboration setup",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/namespaces/create`,
      robots: "noindex, follow"
    },
    'app-edit-namespace': {
      title: "Edit Organization - Advocacy Lab",
      description: "Edit your organization settings and manage team members.",
      keywords: "advocacy lab edit organization, team management, organization settings",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/app/namespaces/edit`,
      robots: "noindex, follow"
    },
    
    'templates-landing': {
      title: "500+ Political Templates - Advocacy Lab | Campaign & Advocacy Designs",
      description: "Browse 500+ professional political templates for campaigns, advocacy, and political organizations. Ready-to-use designs for every need.",
      keywords: "political templates, campaign templates, advocacy templates, political design, campaign graphics, political marketing",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/templates`,
      robots: "index, follow"
    },
    'done-for-you-landing': {
      title: "Done For You Services - Advocacy Lab | Professional Campaign Design",
      description: "Get professional political campaign designs created for you. Custom templates, graphics, and marketing materials for your advocacy campaigns.",
      keywords: "done for you political design, custom campaign templates, political graphic design services, advocacy design services",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/done-for-you`,
      robots: "index, follow"
    },
    // Survey pages - public testimonial collection pages
    'survey': {
      title: "Share Your Story - Advocacy Lab",
      description: "Share your voice and help amplify important stories. Record a quick video to make an impact and support meaningful causes.",
      keywords: "video testimonial, supporter story, advocacy campaign, share your voice, political testimonial",
      ogType: "website",
      twitterCard: "summary_large_image",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Share Your Story",
        "description": "Record a video testimonial to support important causes and advocacy campaigns",
        "provider": {
          "@type": "Organization",
          "name": "Advocacy Lab",
          "url": "https://advocacylab.com"
        },
        "about": {
          "@type": "Thing",
          "name": "Video Testimonial Collection",
          "description": "Platform for collecting authentic supporter stories and testimonials"
        }
      }
    },
    // Checkout and success pages - should be noindex
    'checkout-success': {
      title: "Payment Successful - Advocacy Lab",
      description: "Your payment was successful. Welcome to Advocacy Lab premium features and templates.",
      keywords: "payment success, subscription activated, premium access",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/success`,
      robots: "noindex, follow"
    },
    'checkout-cancel': {
      title: "Payment Cancelled - Advocacy Lab",
      description: "Your payment was cancelled. You can try again or contact support for assistance.",
      keywords: "payment cancelled, checkout cancelled, subscription help",
      canonicalUrl: `${SEO_CONFIG.siteUrl}/cancel`,
      robots: "noindex, follow"
    }
  };

  const config = { ...baseConfig, ...pageConfigs[pageType], ...customData };
  
  // Ensure canonical URL is set
  if (!config.canonicalUrl.includes(SEO_CONFIG.siteUrl)) {
    config.canonicalUrl = `${SEO_CONFIG.siteUrl}${config.canonicalUrl.startsWith('/') ? '' : '/'}${config.canonicalUrl}`;
  }

  return config;
};

// Performance optimization for images
export const getOptimizedImageUrl = (imagePath, width = null, quality = 85) => {
  // If using a CDN, add optimization parameters
  if (imagePath.startsWith('/images/')) {
    const params = [];
    if (width) params.push(`w=${width}`);
    params.push(`q=${quality}`);
    
    // For future CDN integration
    return imagePath; // + (params.length ? `?${params.join('&')}` : '');
  }
  return imagePath;
};

// Generate FAQ structured data
export const generateFAQStructuredData = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Generate breadcrumb structured data
export const generateBreadcrumbStructuredData = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${SEO_CONFIG.siteUrl}${crumb.url}`
    }))
  };
};

// Common FAQ data for political creative platform
export const COMMON_FAQS = [
  {
    question: "What is Advocacy Lab?",
    answer: "Advocacy Lab is a political creative platform that provides 500+ professional templates for political campaigns, advocacy groups, and causes to create high-impact graphics and ads."
  },
  {
    question: "How many templates are available?",
    answer: "We offer over 300 professionally designed templates specifically created for political campaigns, elections, and advocacy work."
  },
  {
    question: "Do you offer a free trial?",
    answer: "Yes, we offer a free trial so you can explore our platform and templates before committing to a subscription. This gives you the opportunity to test our features and see how they work for your campaign."
  },
  {
    question: "Can I customize the templates?",
    answer: "Absolutely! All templates are fully customizable. You can change colors, text, images, and layouts to match your campaign or organization's branding."
  },
  {
    question: "What file formats can I download?",
    answer: "Templates can be downloaded in various formats including PNG, JPG, and PDF, optimized for both digital and print use."
  }
];

// Generate noscript content for SEO bots that don't execute JavaScript
export const generateNoScriptContent = () => {
  return `
    <div style="text-align: center; padding: 40px 20px; font-family: system-ui, -apple-system, sans-serif;">
      <h1 style="color: #1f2937; margin-bottom: 20px;">Advocacy Lab - Political Creative Platform</h1>
      <p style="color: #6b7280; font-size: 18px; line-height: 1.6; max-width: 600px; margin: 0 auto 30px;">
        The fastest way to launch high-impact political creative. Featuring 500+ templates for campaigns and causes. 
        Create professional ads and graphics in minutes.
      </p>
      <div style="margin: 30px 0;">
        <h2 style="color: #1f2937; margin-bottom: 15px;">Key Features:</h2>
        <ul style="text-align: left; max-width: 400px; margin: 0 auto; color: #4b5563;">
          <li>500+ Professional Political Templates</li>
          <li>Drag & Drop Editor</li>
          <li>Multiple File Formats (PNG, JPG, PDF)</li>
          <li>Brand Customization Tools</li>
          <li>Print & Digital Ready Designs</li>
        </ul>
      </div>
      <p style="color: #6b7280; margin-top: 30px;">
        <a href="/templates" style="color: #9333ea; text-decoration: none;">Browse Templates</a> | 
        <a href="/pricing" style="color: #9333ea; text-decoration: none;">View Pricing</a> | 
        <a href="/about" style="color: #9333ea; text-decoration: none;">Learn More</a>
      </p>
    </div>
  `;
};

// Function to create meta refresh as fallback (if needed)
export const createMetaRefresh = (url, delay = 0) => {
  return `<meta http-equiv="refresh" content="${delay};url=${url}">`;
};

// Generate sitemap data for all major pages
export const getSitemapPages = () => {
  return [
    {
      url: '/',
      changefreq: 'daily',
      priority: '1.0',
      lastmod: new Date().toISOString().split('T')[0]
    },
    {
      url: '/about',
      changefreq: 'weekly',
      priority: '0.8',
      lastmod: new Date().toISOString().split('T')[0]
    },
    {
      url: '/templates',
      changefreq: 'daily',
      priority: '0.9',
      lastmod: new Date().toISOString().split('T')[0]
    },
    {
      url: '/pricing',
      changefreq: 'weekly',
      priority: '0.9',
      lastmod: new Date().toISOString().split('T')[0]
    },
    {
      url: '/done-for-you',
      changefreq: 'weekly',
      priority: '0.7',
      lastmod: new Date().toISOString().split('T')[0]
    },
    {
      url: '/demo-templates',
      changefreq: 'weekly',
      priority: '0.6',
      lastmod: new Date().toISOString().split('T')[0]
    },
    
  ];
};

// Generate complete XML sitemap content
export const generateSitemapXML = (pages = getSitemapPages()) => {
  const currentDate = new Date().toISOString().split('T')[0];
  
  const urlEntries = pages.map(page => `  <url>
    <loc>https://advocacylab.com${page.url}</loc>
    <changefreq>${page.changefreq}</changefreq>
    <priority>${page.priority}</priority>
    <lastmod>${page.lastmod || currentDate}</lastmod>
  </url>`).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:news="http://www.google.com/schemas/sitemap-news/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml" xmlns:mobile="http://www.google.com/schemas/sitemap-mobile/1.0" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1" xmlns:video="http://www.google.com/schemas/sitemap-video/1.1">
${urlEntries}
</urlset>`;
};

export default {
  SEO_CONFIG,
  getPageSEO,
  getOptimizedImageUrl,
  generateFAQStructuredData,
  generateBreadcrumbStructuredData,
  COMMON_FAQS,
  isSearchEngineBot,
  needsStaticContent,
  getRobotMetaTags,
  getCanonicalUrl,
  getPreloadHints,
  generateNoScriptContent,
  createMetaRefresh,
  getSitemapPages,
  generateSitemapXML
}; 